"use client"

import styled from "@emotion/styled"
import { IconButton } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs from "dayjs"
import "dayjs/locale/he"
import { createContext, useContext } from "react"
import { LeftArrowIos, RightArrowIos } from "../icons"
import InlineTimeDropdown from "./InlineTimeDropdown"

const StyledCalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 15px;
  width: 100%;
`

const StyledNavButton = styled(IconButton)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 4px;
  color: #0e2846;
  font-family: var(--simpler-pro-full) !important;
  font-size: 14px;
  font-weight: 300;
  &:hover {
    background-color: rgba(0, 0, 250, 0.04);
  }
  svg {
    width: 16px;
    height: 16px;
  }
`

const StyledMonthLabel = styled.span`
  font-family: var(--simpler-pro-full) !important;
  font-weight: 700;
  font-size: 18px;
  color: #0e2846;
`

export function CustomCalendarHeader(props: any) {
  const { currentMonth, onMonthChange } = props
  const monthLabel = dayjs(currentMonth).locale("he").format("MMMM YYYY")

  return (
    <StyledCalendarHeader>
      <StyledNavButton
        onClick={() => onMonthChange(currentMonth.subtract(1, "month"), "right")}
        disableRipple
      >
        <RightArrowIos color="#0e2846" />
        קודם
      </StyledNavButton>
      <StyledMonthLabel>{monthLabel}</StyledMonthLabel>
      <StyledNavButton
        onClick={() => onMonthChange(currentMonth.add(1, "month"), "left")}
        disableRipple
      >
        הבא
        <LeftArrowIos color="#0e2846" />
      </StyledNavButton>
    </StyledCalendarHeader>
  )
}

export type TimePickerContextType = {
  startTime: string
  endTime: string
  startTimeSlots: string[]
  endTimeSlots: string[]
  disabledTimeSlots?: Set<string>
  duration: string
  // eslint-disable-next-line no-unused-vars
  onStartTimeChange: (val: string) => void
  // eslint-disable-next-line no-unused-vars
  onEndTimeChange: (val: string) => void
  onConfirm: () => void
}

export const TimePickerContext = createContext<TimePickerContextType | null>(null)

const StyledTimeField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledTimeLabel = styled.span`
  font-size: 16px;
  font-weight: 300;
  color: #0e2846;
  line-height: 1.5;
`

export const StyledDuration = styled.div`
  font-family: var(--simpler-pro-full) !important;
  font-size: 16px;
  font-weight: 300;
  color: #0e2846;
  margin-top: 4px;
`

export const StyledCalendarTimeRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 8px 16px;
`

export const StyledMobileCalendar = styled.div`
  border: 1.2px solid #e6e6e6;
  border-radius: 8px;
  background-color: white;

  .MuiPickersLayout-root {
    min-width: unset;
  }

  .MuiDateCalendar-root {
    width: 100%;
    max-height: none;
    height: auto;
  }

  .MuiPickersSlideTransition-root {
    min-height: 178px;
  }

  .MuiDayCalendar-header {
    justify-content: space-between;
    padding: 0 8px;
  }

  .MuiDayCalendar-weekDayLabel {
    font-family: var(--simpler-pro-full) !important;
    font-weight: 700;
    font-size: 18px;
    color: #0e2846;
    width: 30px;
    height: 22px;
  }

  .MuiDayCalendar-weekContainer {
    justify-content: space-between;
    gap: 14px;
  }

  .MuiDayCalendar-monthContainer {
    padding: 0 16px 16px;
  }

  .MuiPickersDay-root {
    font-family: var(--simpler-pro-full) !important;
    font-weight: 300;
    font-size: 16px;
    color: #2a2a2a;

    &.Mui-selected {
      background-color: #0000fa;
      color: white;
      border-radius: 50%;
      &:hover {
        background-color: #0000fa;
      }
    }

    &.MuiPickersDay-today {
      border: 1px solid #2a2a2a;
      border-radius: 50%;
      background-color: transparent;
      &:not(.Mui-selected) {
        border: 1px solid #2a2a2a;
      }
    }
  }
`

export const StyledConfirmButton = styled.button`
  width: calc(100% - 32px);
  margin: 8px 16px 16px;
  height: 37px;
  border-radius: 45px;
  border: 1px solid #0e2846;
  background-color: white;
  color: #0e2846;
  font-family: var(--simpler-pro-full) !important;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`

export const StyledDatePicker = styled(DatePicker)`
  .MuiInputBase-root {
    background-color: white;
    border: 1.2px solid #e6e6e6;
    border-radius: 12px;
    height: 48px;

    &:before,
    &:after {
      display: none !important;
    }

    &:hover {
      background-color: white;
    }

    &.Mui-focused {
      background-color: white;
      border-color: #0e2846;
    }

    .MuiInputAdornment-root {
      max-height: max-content;
      width: max-content;
      height: 100%;
      width: 20px;
      height: 20px;
      align-self: center;
    }
  }

  .MuiInputBase-input {
    padding: 12px 16px;
    font-size: 16px;

    &::placeholder {
      color: #687079;
      opacity: 1;
    }
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiInputAdornment-root {
    margin-left: 8px;
    margin-right: 0;
  }
`

/**
 * Custom day component that applies strikethrough styling
 * only to future disabled dates (e.g. Fri/Sat closed days).
 * Past disabled dates keep MUI's default gray style.
 */
export function StrikethroughPickersDay(props: any) {
  const { PickersDay: MuiPickersDay } = require("@mui/x-date-pickers")
  const { day, disabled, outsideCurrentMonth, ...other } = props
  const isFutureDisabled =
    disabled && !outsideCurrentMonth && day && dayjs(day).isAfter(dayjs(), "day")

  return (
    <MuiPickersDay
      {...other}
      day={day}
      disabled={disabled}
      outsideCurrentMonth={outsideCurrentMonth}
      sx={
        isFutureDisabled
          ? { textDecoration: "line-through !important", color: "#b0b0b0 !important" }
          : undefined
      }
    />
  )
}

export const datePickerSlotProps = {
  popper: {
    sx: {
      "& .MuiPaper-root": {
        backgroundColor: "white",
        border: "1.2px solid #e6e6e6",
        borderRadius: "8px",
        boxShadow: "0px 4px 12px rgba(42, 42, 42, 0.12)",
        height: "auto !important",
      },
      "& .MuiDateCalendar-root": {
        maxHeight: "none",
        height: "auto",
      },
      "& .MuiPickersSlideTransition-root": {
        minHeight: "200px",
      },
      "& .MuiDayCalendar-header": {
        justifyContent: "space-between",
        padding: "0 8px",
        "& .MuiDayCalendar-weekDayLabel": {
          fontFamily: "var(--simpler-pro-full) !important",
          fontWeight: 700,
          fontSize: "18px",
          color: "#0e2846",
          width: "30px",
          height: "22px",
        },
      },
      "& .MuiDayCalendar-monthContainer": {
        padding: "0 16px 16px",
      },
      "& .MuiDayCalendar-weekContainer": {
        justifyContent: "space-between",
        gap: "14px",
      },
      "& .MuiPickersDay-root": {
        fontFamily: "var(--simpler-pro-full) !important",
        fontWeight: 300,
        fontSize: "16px",
        color: "#2a2a2a",
        width: "30px",
        height: "30px",
        margin: 0,
        "&:hover": {
          backgroundColor: "rgba(0, 0, 250, 0.08)",
        },
        "&.Mui-selected": {
          backgroundColor: "#0000fa",
          color: "white",
          borderRadius: "50%",
          "&:hover": {
            backgroundColor: "#0000fa",
          },
        },
        "&.MuiPickersDay-today": {
          border: "1px solid #2a2a2a",
          borderRadius: "50%",
          backgroundColor: "transparent",
          "&:not(.Mui-selected)": {
            border: "1px solid #2a2a2a",
          },
        },
      },
    },
  },
  textField: {
    placeholder: "בחר תאריך",
    fullWidth: true,
    inputProps: { readOnly: true, style: { cursor: "pointer" } },
  },
}

export function CalendarWithTimeLayout(props: any) {
  const { children } = props
  const timeCtx = useContext(TimePickerContext)

  return (
    <div>
      {children}
      {timeCtx && (
        <StyledCalendarTimeRow>
          <StyledTimeField>
            <StyledTimeLabel>שעת התחלה</StyledTimeLabel>
            <InlineTimeDropdown
              value={timeCtx.startTime}
              placeholder="בחר שעה"
              options={timeCtx.startTimeSlots}
              disabledOptions={timeCtx.disabledTimeSlots}
              onChange={timeCtx.onStartTimeChange}
            />
          </StyledTimeField>
          <StyledTimeField>
            <StyledTimeLabel>שעת סיום</StyledTimeLabel>
            <InlineTimeDropdown
              value={timeCtx.endTime}
              placeholder="בחר שעה"
              options={timeCtx.endTimeSlots}
              disabledOptions={timeCtx.disabledTimeSlots}
              onChange={timeCtx.onEndTimeChange}
            />
          </StyledTimeField>
        </StyledCalendarTimeRow>
      )}
      {timeCtx?.duration && (
        <StyledDuration style={{ padding: "0 16px 4px", textAlign: "center" }}>
          משך זמן- <span style={{ fontWeight: 700 }}>{timeCtx.duration}</span>
        </StyledDuration>
      )}
      {timeCtx && timeCtx.startTime && timeCtx.endTime && (
        <StyledConfirmButton onClick={timeCtx.onConfirm}>אישור</StyledConfirmButton>
      )}
    </div>
  )
}
